import type { Command } from "../document/commands.ts";
import { IGNORED_ISSUES_MAX, isIssueKind, issueIgnoreKey, readIgnoreKey, staleIgnoreKeys, type IssueTarget } from "../document/issue-ignores.ts";
import type { IssueIgnores, MapDocument } from "../document/types.ts";
import type { ValidationIssue } from "./adapter.ts";

/**
 * Ignoring an issue (ADR-070's amendment of 2026-10-05): which issues may be ignored, which record of a map
 * hides an issue, and the commands that ignore and stop ignoring, each dropping the map's stale records in the
 * same step. Nothing else drops them: a stale record hides nothing, never counts toward the bound, and a save
 * leaves it, since dropping it there would be a history step the author did not make. Only a warning whose
 * check declares a kind and says it may be ignored ever is; what stops the compile never is, whatever the
 * issue or the record says. Nothing here is read by the checks or the compile: the record is applied where
 * lines are shown.
 */

/**
 * The record that hides a line: its key, and whether it is the map's record for the whole kind rather than
 * the line's own. It says nothing of who ignored the line or when: the document holds neither.
 */
export interface IssueIgnored {
  readonly key: string;
  readonly whole: boolean;
}

/**
 * What asking to ignore a line did: it is ignored; the line may not be ignored; or the map already holds as
 * many ignores as it may.
 */
export type IssueIgnoreOutcome = "ignored" | "refused" | "limit";

/**
 * Whether an issue may be ignored: a warning or an info whose check declares a kind (`isIssueKind`) and sets
 * `ignorable`. A refusal never may, though it is flagged.
 *
 * @param issue The issue.
 * @returns True when the author may ignore it.
 */
export function issueIgnorable(issue: ValidationIssue): boolean {
  return (issue.severity === "warning" || issue.severity === "info") && issue.ignorable === true && isIssueKind(issue.kind);
}

/**
 * Where an issue points, from the ids it carries: its prop, its marker, its door, its layer, else the map. An id
 * the map no longer holds falls back to its layer, then the map, as the Issues list's line does.
 *
 * @param map The map.
 * @param issue The issue.
 * @returns The target.
 */
export function issueTargetOf(map: MapDocument, issue: ValidationIssue): IssueTarget {
  const layer = issue.layerId === undefined ? undefined : map.layers.find((candidate) => candidate.id === issue.layerId);
  if (layer?.kind === "prop" && issue.propId !== undefined && layer.props.some((prop) => prop.id === issue.propId)) {
    return { kind: "prop", layerId: layer.id, propId: issue.propId };
  }
  if (layer?.kind === "entity" && issue.entityId !== undefined && layer.entities.some((entity) => entity.id === issue.entityId)) {
    return { kind: "marker", layerId: layer.id, entityId: issue.entityId };
  }
  if (layer?.kind === "door" && issue.doorId !== undefined && layer.doors.some((door) => door.id === issue.doorId)) {
    return { kind: "door", layerId: layer.id, doorId: issue.doorId };
  }
  return layer === undefined ? { kind: "map" } : { kind: "layer", layerId: layer.id };
}

/**
 * The record of a map that hides an issue of a kind on a target: the target's own first, then the map's for
 * the whole kind.
 *
 * @param ignores The map's record, if it has one.
 * @param kind The issue's kind.
 * @param target The issue's target.
 * @returns The record, or null when neither is held.
 */
export function ignoredBy(ignores: IssueIgnores | undefined, kind: string, target: IssueTarget): IssueIgnored | null {
  if (ignores === undefined) {
    return null;
  }
  const own = issueIgnoreKey(kind, target);
  const whole = issueIgnoreKey(kind, { kind: "map" });
  for (const key of own === whole ? [whole] : [own, whole]) {
    if (Object.hasOwn(ignores, key)) {
      return { key, whole: key === whole };
    }
  }
  return null;
}

/**
 * The issues left once the ignored ones of a map are taken out, as the inspector's Issues section shows them.
 * An issue of another map passes through.
 *
 * @param map The map.
 * @param issues The issues, as the checks return them.
 * @returns The issues that are not ignored, in order.
 */
export function shownIssues(map: MapDocument, issues: readonly ValidationIssue[]): readonly ValidationIssue[] {
  const ignores = map.ignoredIssues;
  if (ignores === undefined) {
    return issues;
  }
  return issues.filter((issue) => issue.mapId !== map.id || !issueIgnorable(issue) || ignoredBy(ignores, issue.kind as string, issueTargetOf(map, issue)) === null);
}

/**
 * The command that takes a map's record to another, or null when the two are equal.
 *
 * @param map The map.
 * @param next The keys to leave.
 * @returns The command, its record's keys sorted.
 */
function recordCommand(map: MapDocument, next: ReadonlySet<string>): Command | null {
  const before = map.ignoredIssues ?? {};
  const after: IssueIgnores = Object.fromEntries([...next].sort().map((key) => [key, true as const]));
  const same = Object.keys(before).length === next.size && Object.keys(before).every((key) => next.has(key));
  return same ? null : { kind: "setIssueIgnores", mapId: map.id, before, after };
}

/**
 * The keys of a map's record without its stale ones.
 *
 * @param map The map.
 * @returns The keys that still name something the map holds.
 */
function live(map: MapDocument): Set<string> {
  const stale = new Set(staleIgnoreKeys(map));
  return new Set(Object.keys(map.ignoredIssues ?? {}).filter((key) => !stale.has(key)));
}

/**
 * Ignores issues by their keys, as one command that also drops the map's stale records.
 *
 * @param map The map.
 * @param keys The keys to ignore (`issueIgnoreKey`).
 * @returns The command; null when every key is already held and nothing is stale; or `limit` when a key is
 *   new and the map would hold more than `IGNORED_ISSUES_MAX`.
 * @throws Error when a key does not read as a kind and a target.
 */
export function ignoreIssuesCommand(map: MapDocument, keys: readonly string[]): Command | "limit" | null {
  const next = live(map);
  let added = 0;
  for (const key of keys) {
    if (readIgnoreKey(key) === null) {
      throw new Error(`${JSON.stringify(key)} is not an issue's kind and target`);
    }
    if (!next.has(key)) {
      next.add(key);
      added += 1;
    }
  }
  return added > 0 && next.size > IGNORED_ISSUES_MAX ? "limit" : recordCommand(map, next);
}

/**
 * Stops ignoring, as one command that also drops the map's stale records.
 *
 * @param map The map.
 * @param keys The keys to stop ignoring, or null for every one.
 * @returns The command, or null when the map holds none of them.
 */
export function stopIgnoringCommand(map: MapDocument, keys: readonly string[] | null): Command | null {
  if (keys === null) {
    return recordCommand(map, new Set());
  }
  const held = map.ignoredIssues ?? {};
  if (!keys.some((key) => Object.hasOwn(held, key))) {
    return null;
  }
  const next = live(map);
  keys.forEach((key) => next.delete(key));
  return recordCommand(map, next);
}
